import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import signalApi from '../../../api/signalApi';
import { SignalResponse } from '../../../api/types';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';

interface AdminSignalModalProps {
    open: boolean;
    onClose: () => void;
    signal?: SignalResponse | null;
    businessId?: string;
}

const SIGNAL_TYPES = ['cashflow', 'margin', 'bill', 'invoice'] as const;
const PRIORITIES = [
    { value: 1, label: 'High', color: 'text-red-500 bg-red-50 border-red-200' },
    { value: 2, label: 'Medium', color: 'text-orange-500 bg-orange-50 border-orange-200' },
    { value: 3, label: 'Low', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
];
const RESOLVED_OPTS = ['open', 'closed', 'archived'] as const;

function buildInitialForm(signal?: SignalResponse | null, businessId?: string) {
    return {
        business_id: businessId || '',
        type: (signal?.type ?? 'cashflow') as string,
        priority: (signal?.priority ?? 2) as 1 | 2 | 3,
        title: signal?.title ?? '',
        impact: signal?.impact ?? '',
        details: signal?.details ?? '',
        projected_outcome: signal?.projected_outcome ?? '',
        resolved: (signal?.resolved ?? 'open') as 'open' | 'closed' | 'archived',
        action_link: signal?.action_link ?? '',
        event_date: signal?.event_date ? signal.event_date.slice(0, 10) : '',
        due_on: signal?.due_on ? signal.due_on.slice(0, 10) : '',
        visualJson: signal?.visual ? JSON.stringify(signal.visual, null, 2) : '',
        metadataJson: signal?.metadata ? JSON.stringify(signal.metadata, null, 2) : '',
    };
}

type FormState = ReturnType<typeof buildInitialForm>;

interface FieldProps {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}
const Field = ({ label, required, children }: FieldProps) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const inputCls = "w-full px-3 py-2.5 text-sm text-gray-800 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none bg-white transition-shadow";
const textareaCls = `${inputCls} resize-none`;

export default function AdminSignalModal({ open, onClose, signal, businessId }: AdminSignalModalProps) {
    const queryClient = useQueryClient();
    const [form, setForm] = useState<FormState>(() => buildInitialForm(signal, businessId));
    const [error, setError] = useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(buildInitialForm(signal, businessId));
            setError(null);
            setShowAdvanced(false);
        }
    }, [open, signal, businessId]);

    const set = (field: keyof FormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const setPriority = (p: 1 | 2 | 3) => setForm(prev => ({ ...prev, priority: p }));

    const buildPayload = () => {
        let visual: any = undefined;
        let metadata: any = undefined;

        if (form.visualJson.trim()) {
            try { visual = JSON.parse(form.visualJson); }
            catch { throw new Error('Visual JSON is invalid.'); }
        }
        if (form.metadataJson.trim()) {
            try { metadata = JSON.parse(form.metadataJson); }
            catch { throw new Error('Metadata JSON is invalid.'); }
        }

        return {
            business_id: form.business_id || undefined,
            type: form.type,
            priority: form.priority,
            title: form.title,
            impact: form.impact,
            details: form.details,
            projected_outcome: form.projected_outcome || undefined,
            resolved: form.resolved,
            action_link: form.action_link || undefined,
            event_date: form.event_date ? new Date(form.event_date).toISOString() : undefined,
            due_on: form.due_on ? new Date(form.due_on).toISOString() : undefined,
            ...(visual && { visual }),
            ...(metadata && { metadata }),
        };
    };

    const createMutation = useMutation({
        mutationFn: (payload: any) => signalApi.createAdminSignal(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
            onClose();
        },
        onError: (err: any) => setError(getUserFriendlyErrorMessage(err, 'Failed to create signal.')),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: any }) =>
            signalApi.updateAdminSignal(id, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
            onClose();
        },
        onError: (err: any) => setError(getUserFriendlyErrorMessage(err, 'Failed to update signal.')),
    });

    if (!open) return null;

    const handleSave = () => {
        setError(null);
        if (!form.title.trim()) { setError('Title is required.'); return; }
        if (!form.details.trim()) { setError('Details is required.'); return; }
        let payload: any;
        try { payload = buildPayload(); } catch (e: any) { setError(e.message); return; }

        if (signal?._id) {
            updateMutation.mutate({ id: signal._id, payload });
        } else {
            createMutation.mutate(payload);
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;
    const isEditing = !!signal;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-base font-bold text-gray-800">
                            {isEditing ? 'Edit Signal' : 'Create Signal'}
                        </h2>
                        <p className="text-xs text-gray-400 mt-0.5">Admin override</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {error && (
                        <div className="bg-red-50 text-red-600 px-3 py-2.5 rounded-lg text-sm flex items-start gap-2 border border-red-100">
                            <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Type + Priority row */}
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Type" required>
                            <select value={form.type} onChange={set('type')} className={inputCls}>
                                {SIGNAL_TYPES.map(t => (
                                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Priority" required>
                            <div className="flex gap-2">
                                {PRIORITIES.map(p => (
                                    <button
                                        key={p.value}
                                        type="button"
                                        onClick={() => setPriority(p.value as 1 | 2 | 3)}
                                        className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${form.priority === p.value
                                            ? p.color
                                            : 'text-gray-400 bg-gray-50 border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </div>

                    {/* Status + Business ID */}
                    {/* <div className="grid grid-cols-2 gap-4">
                        <Field label="Status">
                            <select value={form.resolved} onChange={set('resolved')} className={inputCls}>
                                {RESOLVED_OPTS.map(r => (
                                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Business ID">
                            <input
                                type="text"
                                value={form.business_id}
                                onChange={set('business_id')}
                                placeholder="Auto-selected"
                                className={inputCls}
                            />
                        </Field>
                    </div> */}

                    {/* Title */}
                    <Field label="Title" required>
                        <input
                            type="text"
                            value={form.title}
                            onChange={set('title')}
                            placeholder="e.g. Cash flow shortfall detected"
                            className={inputCls}
                        />
                    </Field>

                    {/* Impact */}
                    <Field label="Impact Summary">
                        <input
                            type="text"
                            value={form.impact}
                            onChange={set('impact')}
                            placeholder="e.g. Revenue dropped 12% vs last month"
                            className={inputCls}
                        />
                    </Field>

                    {/* Details */}
                    <Field label="Details" required>
                        <textarea
                            value={form.details}
                            onChange={set('details')}
                            rows={3}
                            placeholder="Provide context for the signal..."
                            className={textareaCls}
                        />
                    </Field>

                    {/* Projected Outcome */}
                    <Field label="Projected Outcome">
                        <textarea
                            value={form.projected_outcome}
                            onChange={set('projected_outcome')}
                            rows={2}
                            placeholder="What happens if resolved?"
                            className={textareaCls}
                        />
                    </Field>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Event Date">
                            <input type="date" value={form.event_date} onChange={set('event_date')} className={inputCls} />
                        </Field>
                        <Field label="Due On">
                            <input type="date" value={form.due_on} onChange={set('due_on')} className={inputCls} />
                        </Field>
                    </div>

                    {/* Action Link */}
                    <Field label="Action Link">
                        <input
                            type="text"
                            value={form.action_link}
                            onChange={set('action_link')}
                            placeholder="/margin or /ledger"
                            className={inputCls}
                        />
                    </Field>

                    {/* Advanced: Visual + Metadata JSON */}
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(v => !v)}
                            className="w-full flex justify-between items-center px-4 py-3 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-colors"
                        >
                            Advanced (Visual & Metadata JSON)
                            {showAdvanced ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                        {showAdvanced && (
                            <div className="px-4 pb-4 space-y-4 bg-gray-50/60 border-t border-gray-100">
                                <div className="pt-3">
                                    <Field label='Visual JSON (type: "sparkline" | "table")'>
                                        <textarea
                                            value={form.visualJson}
                                            onChange={set('visualJson')}
                                            rows={5}
                                            placeholder={'{\n  "type": "sparkline",\n  "data": { "points": [1,2,3], "labels": ["Jan","Feb","Mar"] }\n}'}
                                            className={`${textareaCls} font-mono text-xs`}
                                            spellCheck={false}
                                        />
                                    </Field>
                                </div>
                                {/* <Field label="Metadata JSON (arbitrary key-value)">
                                    <textarea
                                        value={form.metadataJson}
                                        onChange={set('metadataJson')}
                                        rows={3}
                                        placeholder={'{\n  "trigger_value": 12500,\n  "metric_source": "stripe"\n}'}
                                        className={`${textareaCls} font-mono text-xs`}
                                        spellCheck={false}
                                    />
                                </Field> */}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-white">
                    <button
                        onClick={onClose}
                        disabled={isPending}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isPending}
                        className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm disabled:opacity-60"
                    >
                        <Save size={15} />
                        {isPending ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Signal'}
                    </button>
                </div>
            </div>
        </div>
    );
}
